// ─────────────────────────────────────────────────────────────────────────────
// fields.js — the form atoms shared by every Settings tab (Field/Input/Select/
// Toggle/SectionTitle/Divider) plus the load + banner states. Presentational
// only: no data fetching, no page state.
// ─────────────────────────────────────────────────────────────────────────────
import styles from "./fields.module.css";

export function SectionTitle({ children }) {
  return <h3 className={styles.sectionTitle}>{children}</h3>;
}

export function SectionSub({ children }) {
  return <p className={styles.sectionSub}>{children}</p>;
}

export function Divider() {
  return <div className={styles.divider}/>;
}

export function Field({ label, children, hint }) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      {children}
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </div>
  );
}

export function FieldList({ children }) {
  return <div className={styles.fieldList}>{children}</div>;
}

export function Input({ value, onChange, placeholder, type = "text", readOnly }) {
  return (
    <input
      className={`${styles.input} ${readOnly ? styles.inputReadOnly : ""}`}
      type={type}
      value={value ?? ""}
      onChange={e => onChange?.(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
    />
  );
}

export function Select({ value, onChange, options, disabled }) {
  return (
    <select
      className={`${styles.input} ${disabled ? styles.inputReadOnly : ""}`}
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">— Select —</option>
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  );
}

/**
 * Switch — the bare control, no label row around it. Use directly when the
 * surrounding layout already names the thing being switched (a table row, say);
 * use `Toggle` for the standard label-left / switch-right settings row.
 * `label` is the accessible name only — it is never painted.
 */
export function Switch({ checked, onChange, label, disabled = false }) {
  return (
    <button
      className={`${styles.toggle} ${checked ? styles.toggleOn : ""}`}
      onClick={() => { if (!disabled) onChange(!checked); }}
      disabled={disabled}
      role="switch" aria-checked={checked} aria-label={label}
    >
      <span className={styles.toggleThumb}/>
    </button>
  );
}

export function Toggle({ checked, onChange, label, sub, disabled = false }) {
  return (
    <div className={styles.toggleRow}>
      <div className={styles.toggleText}>
        <span className={styles.toggleLabel}>{label}</span>
        {sub && <span className={styles.toggleSub}>{sub}</span>}
      </div>
      <Switch checked={checked} onChange={onChange} label={label} disabled={disabled}/>
    </div>
  );
}

export function ToggleList({ children }) {
  return <div className={styles.toggleList}>{children}</div>;
}

export function LoadingPane() {
  return <div className={styles.loadingPane}>Loading…</div>;
}

export function ErrorBanner({ children }) {
  return <div className={styles.errorBanner}>⚠ {children}</div>;
}

export function SuccessBanner({ children }) {
  return <div className={styles.successBanner}>✓ {children}</div>;
}
